import { router } from "@inertiajs/react"
import { Check, Loader2, Pencil, X } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { errorMessages } from "@/lib/form-errors"
import { cn } from "@/lib/utils"

import { formatDate } from "./status-chip"
import type { TimelineStep } from "./types"

/**
 * Cradle to grave, one row per stamp the SUBCLASS declares — so a requal never
 * renders an OTMA line and a bad order always does.
 *
 * THE DATE IS THE EDIT. There is no status dropdown anywhere in this product:
 * an operator types the day the dispo came back, the server recomputes the
 * status from the timestamps and the queue re-sorts. That is the whole reason
 * an email-driven update is a one-field write.
 *
 * `dwell_days` between two filled stamps is the number that says where the time
 * actually went — 3 days in transit and 46 waiting on a disposition is a very
 * different story from the reverse, and the totals alone can't tell them apart.
 */
export function WorkOrderTimeline({
  steps,
  updatePath,
  canEdit,
}: {
  steps: TimelineStep[]
  updatePath: string
  canEdit: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="relative">
          {steps.map((step, index) => (
            <TimelineRow
              key={step.column}
              step={step}
              last={index === steps.length - 1}
              updatePath={updatePath}
              canEdit={canEdit}
            />
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

function TimelineRow({
  step,
  last,
  updatePath,
  canEdit,
}: {
  step: TimelineStep
  last: boolean
  updatePath: string
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(() => inputValue(step))
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  function save() {
    if (saving) return
    setSaving(true)
    setErrors([])
    router.patch(
      updatePath,
      // Blank clears the stamp, which walks the status BACK — that's correct,
      // and it's how an operator undoes a date entered on the wrong car.
      { work_order: { [step.column]: value || null } },
      {
        preserveScroll: true,
        onSuccess: () => setEditing(false),
        // WITHOUT THIS A 422 IS SILENT. The server rejects a timeline that does
        // not hold together, and with no handler the row just closed its
        // spinner and stayed on the old date — an operator's correction
        // vanishing with no explanation. Same-day entries are the common case
        // that lands here.
        onError: (received) => setErrors(errorMessages(received)),
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {!last && (
        <span
          className={cn(
            "absolute top-5 bottom-0 left-[7px] w-px",
            step.reached ? "bg-primary/40" : "bg-border",
          )}
          aria-hidden
        />
      )}

      <span
        className={cn(
          "mt-1 size-3.5 shrink-0 rounded-full border-2",
          step.reached
            ? "border-primary bg-primary"
            : "border-muted-foreground/40 bg-background",
        )}
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className={cn(
              "text-sm font-medium",
              !step.reached && "text-muted-foreground",
            )}
          >
            {step.label}
          </span>

          {step.dwell_days != null && (
            <span className="text-muted-foreground text-xs tabular-nums">
              +{step.dwell_days}d
            </span>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5">
            <div className="flex items-center gap-1.5">
            <Input
              type={step.kind === "datetime" ? "datetime-local" : "date"}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              className="h-8 w-52"
              aria-label={step.label}
            />
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setValue(inputValue(step))
                setErrors([])
                setEditing(false)
              }}
            >
              <X className="size-4" />
            </Button>
            </div>
            {/* The row STAYS in edit mode on a 422, so the message sits with
                the value that caused it and the operator can correct it in
                place. */}
            {errors.length > 0 && (
              <ul className="text-destructive mt-1.5 space-y-0.5 text-xs">
                {errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => canEdit && setEditing(true)}
            className={cn(
              "group mt-0.5 flex items-center gap-1.5 text-sm",
              canEdit && "hover:underline",
              !step.reached && "text-muted-foreground",
            )}
          >
            {step.reached ? formatDate(step.value) : "Not yet"}
            {canEdit && (
              <Pencil className="size-3 opacity-0 transition-opacity group-hover:opacity-60" />
            )}
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * Rails ships a date column as "2026-08-03" and a datetime as a full ISO
 * string; the two input types want different slices of that. Anything
 * unparseable becomes an empty input rather than a browser-rejected value.
 */
function inputValue(step: TimelineStep): string {
  if (!step.value) return ""
  if (step.kind === "date") return step.value.slice(0, 10)
  const date = new Date(step.value)
  if (Number.isNaN(date.getTime())) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
